// src/services/WarehouseInventoryService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  writeBatch,
  increment,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { WarehouseInventory, WarehouseAllocation, ProductVariant } from '../../../enums/product';
import { syncSerialNumbers, writeSerialStatus, assertSerialsAvailable } from '../../../utils/serialTracking';

const WAREHOUSE_INVENTORY_COLLECTION = 'warehouse_inventory';
const WAREHOUSES_COLLECTION = 'warehouses';
const PRODUCTS_COLLECTION = 'products';

export class WarehouseInventoryService {
  // Get all inventory for a warehouse
  static async getWarehouseInventory(warehouseId: string): Promise<WarehouseInventory[]> {
    try {
      const q = query(
        collection(db, WAREHOUSE_INVENTORY_COLLECTION, warehouseId, 'products')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastUpdated: doc.data().lastUpdated?.toDate(),
      })) as WarehouseInventory[];
    } catch (error) {
      console.error('Error fetching warehouse inventory:', error);
      throw error;
    }
  }

  // Get inventory for a specific product in a warehouse
  static async getProductInventory(
    warehouseId: string,
    productId: string,
    variantId?: string
  ): Promise<WarehouseInventory | null> {
    try {
      const docId = variantId ? `${productId}_${variantId}` : productId;
      const docRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, warehouseId, 'products', docId);
      const snapshot = await getDoc(docRef);
      
      if (snapshot.exists()) {
        return {
          id: snapshot.id,
          ...snapshot.data(),
          lastUpdated: snapshot.data().lastUpdated?.toDate(),
        } as WarehouseInventory;
      }
      return null;
    } catch (error) {
      console.error('Error fetching product inventory:', error);
      throw error;
    }
  }

  // Allocate product/variant to warehouse (with warehouse capacity update)
  static async allocateProductToWarehouse(
    warehouseId: string,
    productId: string,
    productName: string,
    quantity: number,
    minStockLevel: number,
    maxStockLevel: number,
    sku: string,
    price: number,
    variantId?: string,
    variantName?: string,
    discount?: number,
    avgCost?: number,
    serialNumbers?: string[]
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        // Get warehouse to check capacity
        const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
        const warehouseDoc = await transaction.get(warehouseRef);

        if (!warehouseDoc.exists()) {
          throw new Error('Warehouse not found');
        }

        const warehouseData = warehouseDoc.data();
        const availableCapacity = warehouseData.capacity.available || warehouseData.capacity.total - warehouseData.capacity.used;

        if (quantity > availableCapacity) {
          throw new Error(`Insufficient warehouse capacity. Available: ${availableCapacity}, Requested: ${quantity}`);
        }

        // Create/update inventory record
        const docId = variantId ? `${productId}_${variantId}` : productId;
        const inventoryRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, warehouseId, 'products', docId);
        const inventoryDoc = await transaction.get(inventoryRef);

        let previousQuantity = 0;
        let previousSerials: string[] | undefined;
        if (inventoryDoc.exists()) {
          previousQuantity = inventoryDoc.data().quantity || 0;
          previousSerials = inventoryDoc.data().availableSerials;
        }

        // Guard global serial uniqueness — must happen here, in the read
        // phase, before any writes are issued in this transaction.
        if (serialNumbers) {
          const prevSet = new Set(previousSerials || []);
          const brandNewSerials = serialNumbers.filter(s => !prevSet.has(s));
          await assertSerialsAvailable(transaction, brandNewSerials);
        }

        // Update inventory
        const inventoryData: Record<string, any> = {
          warehouseId,
          productId,
          productName,
          variantId: variantId || null,
          variantName: variantName || null,
          quantity,
          minStockLevel,
          maxStockLevel,
          sku,
          price,
          discount: discount || 0,
          // `price` is always the selling price. `avgCost` is the separate,
          // dedicated purchase-cost field — never overload `price` with cost.
          avgCost: avgCost || 0,
          isOutOfStock: quantity === 0,
          isLowStock: quantity > 0 && quantity <= minStockLevel,
          isOverStock: quantity > 0 && quantity >= maxStockLevel,
          lastUpdated: serverTimestamp(),
        };

        // Serial-tracked products: availableSerials *is* the stock, kept in
        // sync with quantity. Untouched (field absent) for ordinary products.
        if (serialNumbers) {
          inventoryData.availableSerials = serialNumbers;
          syncSerialNumbers(transaction, previousSerials, serialNumbers, {
            productId,
            productName,
            sku,
            variantId,
            variantName,
            warehouseId,
          });
        }

        transaction.set(inventoryRef, inventoryData, { merge: true });

        // Update warehouse capacity
        const quantityDifference = quantity - previousQuantity;
        transaction.update(warehouseRef, {
          'capacity.used': increment(quantityDifference),
          'capacity.available': increment(-quantityDifference),
          'stats.productCount': inventoryDoc.exists() ? increment(0) : increment(1),
          'stats.totalStock': increment(quantityDifference),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error('Error allocating product to warehouse:', error);
      throw error;
    }
  }

  // Receive stock from a purchase order — adds to existing quantity (or
  // creates the inventory row on the very first receipt) and recomputes a
  // weighted-average purchase cost, per answers.text Option 1:
  //   newAvgCost = ((oldAvgCost * oldQty) + (poUnitCost * receivedQty)) / (oldQty + receivedQty)
  // `price` is always set to the product/variant's current selling price —
  // it never carries cost data, unlike the old allocateProductToWarehouse-on-
  // first-receipt path this replaces.
  static async receiveStockWithCost(
    warehouseId: string,
    productId: string,
    productName: string,
    receivedQuantity: number,
    unitCost: number,
    defaultMinStockLevel: number,
    defaultMaxStockLevel: number,
    sku: string,
    sellingPrice: number,
    variantId?: string,
    variantName?: string,
    discount?: number,
    newSerialNumbers?: string[]
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
        const warehouseDoc = await transaction.get(warehouseRef);
        if (!warehouseDoc.exists()) throw new Error('Warehouse not found');

        const docId = variantId ? `${productId}_${variantId}` : productId;
        const inventoryRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, warehouseId, 'products', docId);
        const inventoryDoc = await transaction.get(inventoryRef);

        const existing = inventoryDoc.exists() ? inventoryDoc.data() : null;
        const previousQuantity = existing?.quantity || 0;
        const previousAvgCost = existing?.avgCost ?? 0;

        const newQuantity = previousQuantity + receivedQuantity;
        const newAvgCost = newQuantity > 0
          ? ((previousAvgCost * previousQuantity) + (unitCost * receivedQuantity)) / newQuantity
          : 0;

        const minStockLevel = existing?.minStockLevel ?? defaultMinStockLevel;
        const maxStockLevel = existing?.maxStockLevel ?? defaultMaxStockLevel;

        // Guard global serial uniqueness — read phase, before any writes.
        if (newSerialNumbers && newSerialNumbers.length > 0) {
          await assertSerialsAvailable(transaction, newSerialNumbers);
        }

        const inventoryData: Record<string, any> = {
          warehouseId,
          productId,
          productName,
          variantId: variantId || null,
          variantName: variantName || null,
          quantity: newQuantity,
          minStockLevel,
          maxStockLevel,
          sku,
          price: sellingPrice,
          avgCost: newAvgCost,
          discount: discount ?? existing?.discount ?? 0,
          isOutOfStock: newQuantity === 0,
          isLowStock: newQuantity > 0 && newQuantity <= minStockLevel,
          isOverStock: newQuantity > 0 && newQuantity >= maxStockLevel,
          lastUpdated: serverTimestamp(),
        };

        // Serial-tracked products: the newly received units are appended to
        // whatever's already available, and each gets a fresh 'in_stock'
        // lookup doc. Absent entirely for ordinary (non-serialized) stock.
        if (newSerialNumbers && newSerialNumbers.length > 0) {
          const previousSerials: string[] = existing?.availableSerials || [];
          inventoryData.availableSerials = [...previousSerials, ...newSerialNumbers];
          syncSerialNumbers(transaction, previousSerials, inventoryData.availableSerials, {
            productId,
            productName,
            sku,
            variantId,
            variantName,
            warehouseId,
          });
        }

        transaction.set(inventoryRef, inventoryData, { merge: true });

        transaction.update(warehouseRef, {
          'capacity.used': increment(receivedQuantity),
          'capacity.available': increment(-receivedQuantity),
          'stats.productCount': inventoryDoc.exists() ? increment(0) : increment(1),
          'stats.totalStock': increment(receivedQuantity),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error('Error receiving stock with cost:', error);
      throw error;
    }
  }

  // Remove product allocation from warehouse
  static async removeProductFromWarehouse(
    warehouseId: string,
    productId: string,
    variantId?: string
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const docId = variantId ? `${productId}_${variantId}` : productId;
        const inventoryRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, warehouseId, 'products', docId);
        const inventoryDoc = await transaction.get(inventoryRef);

        if (!inventoryDoc.exists()) {
          return; // Already removed
        }

        const inventoryData = inventoryDoc.data();
        const quantity = inventoryData.quantity || 0;

        // Serial-tracked products: the remaining unsold units are no longer
        // trackable inventory once this allocation is removed, so drop their
        // lookup docs too. No-op for ordinary (non-serialized) products.
        const remainingSerials: string[] = inventoryData.availableSerials || [];
        if (remainingSerials.length > 0) {
          syncSerialNumbers(transaction, remainingSerials, [], {
            productId,
            warehouseId,
          });
        }

        // Delete inventory record
        transaction.delete(inventoryRef);

        // Update warehouse capacity
        const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
        transaction.update(warehouseRef, {
          'capacity.used': increment(-quantity),
          'capacity.available': increment(quantity),
          'stats.productCount': increment(-1),
          'stats.totalStock': increment(-quantity),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error('Error removing product from warehouse:', error);
      throw error;
    }
  }

  // Update product quantity in warehouse
  static async updateProductQuantity(
    warehouseId: string,
    productId: string,
    quantity: number,
    variantId?: string,
    discount?: number
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const docId = variantId ? `${productId}_${variantId}` : productId;
        const inventoryRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, warehouseId, 'products', docId);
        const inventoryDoc = await transaction.get(inventoryRef);

        if (!inventoryDoc.exists()) {
          throw new Error('Product not found in warehouse inventory');
        }

        const inventoryData = inventoryDoc.data();
        const previousQuantity = inventoryData.quantity || 0;
        const minStockLevel = inventoryData.minStockLevel || 0;
        const maxStockLevel = inventoryData.maxStockLevel || 0;

        // Update inventory
        const updateData: any = {
          quantity,
          isOutOfStock: quantity === 0,
          isLowStock: quantity > 0 && quantity <= minStockLevel,
          isOverStock: quantity > 0 && quantity >= maxStockLevel,
          lastUpdated: serverTimestamp(),
        };

        // Update discount if provided
        if (discount !== undefined) {
          updateData.discount = discount;
        }

        transaction.update(inventoryRef, updateData);

        // Update warehouse capacity
        const quantityDifference = quantity - previousQuantity;
        const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
        transaction.update(warehouseRef, {
          'capacity.used': increment(quantityDifference),
          'capacity.available': increment(-quantityDifference),
          'stats.totalStock': increment(quantityDifference),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error('Error updating product quantity:', error);
      throw error;
    }
  }

  // Update min/max stock levels without changing quantity
  static async updateStockLevels(
    warehouseId: string,
    productId: string,
    minStockLevel: number,
    maxStockLevel: number,
    variantId?: string
  ): Promise<void> {
    try {
      const docId = variantId ? `${productId}_${variantId}` : productId;
      const inventoryRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, warehouseId, 'products', docId);
      const inventoryDoc = await getDoc(inventoryRef);

      if (!inventoryDoc.exists()) {
        throw new Error('Product not found in warehouse inventory');
      }

      const inventoryData = inventoryDoc.data();
      const quantity = inventoryData.quantity || 0;

      await updateDoc(inventoryRef, {
        minStockLevel,
        maxStockLevel,
        isLowStock: quantity > 0 && quantity <= minStockLevel,
        isOverStock: quantity > 0 && quantity >= maxStockLevel,
        lastUpdated: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating stock levels:', error);
      throw error;
    }
  }

  // Get products in a warehouse with details
  static async getWarehouseProductsWithDetails(warehouseId: string): Promise<any[]> {
    try {
      const inventory = await this.getWarehouseInventory(warehouseId);
      const productsWithDetails = [];

      for (const item of inventory) {
        try {
          const productRef = doc(db, PRODUCTS_COLLECTION, item.productId);
          const productDoc = await getDoc(productRef);

          if (productDoc.exists()) {
            const productData = productDoc.data();
            productsWithDetails.push({
              ...item,
              productDetails: {
                name: productData.name,
                description: productData.description,
                categoryId: productData.categoryId,
                images: productData.images || [],
                hasVariants: productData.hasVariants || false,
              }
            });
          } else {
            productsWithDetails.push(item);
          }
        } catch (error) {
          console.error(`Error fetching product ${item.productId}:`, error);
          productsWithDetails.push(item);
        }
      }

      return productsWithDetails;
    } catch (error) {
      console.error('Error fetching warehouse products with details:', error);
      throw error;
    }
  }

  // Get warehouse capacity utilization
  static async getWarehouseUtilization(warehouseId: string): Promise<number> {
    try {
      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      const warehouseDoc = await getDoc(warehouseRef);

      if (warehouseDoc.exists()) {
        const data = warehouseDoc.data();
        const total = data.capacity.total || 1; // Avoid division by zero
        const used = data.capacity.used || 0;
        return Math.round((used / total) * 100);
      }
      return 0;
    } catch (error) {
      console.error('Error getting warehouse utilization:', error);
      throw error;
    }
  }

  // Get stock summary for a warehouse
  static async getWarehouseStockSummary(warehouseId: string): Promise<{
    totalProducts: number;
    totalStock: number;
    lowStockCount: number;
    outOfStockCount: number;
    inStockCount: number;
    totalValue: number;
  }> {
    try {
      const inventory = await this.getWarehouseInventory(warehouseId);
      
      let totalProducts = 0;
      let totalStock = 0;
      let lowStockCount = 0;
      let outOfStockCount = 0;
      let totalValue = 0;

      inventory.forEach(item => {
        totalProducts++;
        totalStock += item.quantity;
        
        // Calculate value with discount
        const itemPrice = item.price || 0;
        const itemDiscount = item.discount || 0;
        const discountedPrice = itemPrice - (itemPrice * itemDiscount / 100);
        totalValue += (item.quantity * discountedPrice);
        
        if (item.isLowStock) lowStockCount++;
        if (item.isOutOfStock) outOfStockCount++;
      });

      return {
        totalProducts,
        totalStock,
        lowStockCount,
        outOfStockCount,
        inStockCount: totalProducts - outOfStockCount,
        totalValue,
      };
    } catch (error) {
      console.error('Error getting warehouse stock summary:', error);
      throw error;
    }
  }

  // Transfer stock between warehouses
  static async transferStock(
    fromWarehouseId: string,
    toWarehouseId: string,
    productId: string,
    quantity: number,
    variantId?: string,
    serialNumbersToTransfer?: string[]
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        // ── Phase 1: ALL READS FIRST ────────────────────────────────────────
        // Firestore requires every read to complete before any write is issued.
        // The original code read fromInventory, wrote to it, then read
        // toInventory — that interleaving caused:
        //   "FirebaseError: Firestore transactions require all reads to be
        //    executed before all writes."

        // 1a. Read source inventory
        const fromDocId = variantId ? `${productId}_${variantId}` : productId;
        const fromInventoryRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, fromWarehouseId, 'products', fromDocId);
        const fromInventoryDoc = await transaction.get(fromInventoryRef);

        // 1b. Read destination inventory (may not exist yet — that's fine)
        const toDocId = variantId ? `${productId}_${variantId}` : productId;
        const toInventoryRef = doc(db, WAREHOUSE_INVENTORY_COLLECTION, toWarehouseId, 'products', toDocId);
        const toInventoryDoc = await transaction.get(toInventoryRef);

        // 1c. Read source warehouse
        const fromWarehouseRef = doc(db, WAREHOUSES_COLLECTION, fromWarehouseId);
        const fromWarehouseDoc = await transaction.get(fromWarehouseRef);

        // 1d. Read destination warehouse
        const toWarehouseRef = doc(db, WAREHOUSES_COLLECTION, toWarehouseId);
        const toWarehouseDoc = await transaction.get(toWarehouseRef);

        // ── Phase 2: VALIDATION (pure JS, no Firestore I/O) ─────────────────

        if (!fromInventoryDoc.exists()) {
          throw new Error('Product not found in source warehouse');
        }

        if (!fromWarehouseDoc.exists()) {
          throw new Error('Source warehouse not found');
        }

        if (!toWarehouseDoc.exists()) {
          throw new Error('Destination warehouse not found');
        }

        const fromInventoryData = fromInventoryDoc.data();
        const currentQuantity = fromInventoryData.quantity || 0;

        // Serial-tracked products: the transferred count is always derived
        // from the specific serials chosen, not the raw quantity argument.
        const sourceSerials: string[] = fromInventoryData.availableSerials || [];
        if (serialNumbersToTransfer && serialNumbersToTransfer.length > 0) {
          const missing = serialNumbersToTransfer.filter(s => !sourceSerials.includes(s));
          if (missing.length > 0) {
            throw new Error(`Serial(s) not available in source warehouse: ${missing.join(', ')}`);
          }
        }
        const effectiveQuantity = serialNumbersToTransfer ? serialNumbersToTransfer.length : quantity;

        if (currentQuantity < effectiveQuantity) {
          throw new Error(`Insufficient stock. Available: ${currentQuantity}, Requested: ${effectiveQuantity}`);
        }

        const toWarehouseData = toWarehouseDoc.data();
        const availableCapacity = toWarehouseData.capacity.available || 0;

        if (effectiveQuantity > availableCapacity) {
          throw new Error(`Destination warehouse has insufficient capacity. Available: ${availableCapacity}`);
        }

        const minStockLevel = fromInventoryData.minStockLevel || 0;
        const maxStockLevel = fromInventoryData.maxStockLevel || 0;

        // Compute new quantities
        const newSourceQuantity = currentQuantity - effectiveQuantity;
        const previousDestQuantity = toInventoryDoc.exists()
          ? (toInventoryDoc.data().quantity || 0)
          : 0;
        const newDestQuantity = previousDestQuantity + effectiveQuantity;

        // Transferred stock carries its cost with it — blend it into the
        // destination's existing weighted-average cost (or seed it, if the
        // destination has no prior inventory row for this SKU). Without this,
        // a transfer into a brand-new warehouse row leaves avgCost unset, and
        // every later sale from that stock falls back to cost === sellingPrice.
        const sourceAvgCost = fromInventoryData.avgCost ?? fromInventoryData.price ?? 0;
        const previousDestAvgCost = toInventoryDoc.exists()
          ? (toInventoryDoc.data().avgCost ?? toInventoryDoc.data().price ?? sourceAvgCost)
          : sourceAvgCost;
        const newDestAvgCost = newDestQuantity > 0
          ? ((previousDestAvgCost * previousDestQuantity) + (sourceAvgCost * effectiveQuantity)) / newDestQuantity
          : previousDestAvgCost;

        // ── Phase 3: ALL WRITES ──────────────────────────────────────────────

        // 3a. Update source inventory
        const sourceUpdate: Record<string, any> = {
          quantity: newSourceQuantity,
          isOutOfStock: newSourceQuantity === 0,
          isLowStock: newSourceQuantity > 0 && newSourceQuantity <= minStockLevel,
          isOverStock: newSourceQuantity > 0 && newSourceQuantity >= maxStockLevel,
          lastUpdated: serverTimestamp(),
        };

        let destSerials: string[] | undefined;
        if (serialNumbersToTransfer && serialNumbersToTransfer.length > 0) {
          sourceUpdate.availableSerials = sourceSerials.filter(s => !serialNumbersToTransfer.includes(s));
          const previousDestSerials: string[] = toInventoryDoc.exists()
            ? (toInventoryDoc.data().availableSerials || [])
            : [];
          destSerials = [...previousDestSerials, ...serialNumbersToTransfer];

          for (const serial of serialNumbersToTransfer) {
            writeSerialStatus(transaction, serial, 'in_stock', {
              productId,
              productName: fromInventoryData.productName,
              sku: fromInventoryData.sku,
              variantId,
              variantName: fromInventoryData.variantName,
              warehouseId: toWarehouseId,
              warehouseName: toWarehouseData.name,
            });
          }
        }

        transaction.update(fromInventoryRef, sourceUpdate);

        // 3b. Update source warehouse capacity
        transaction.update(fromWarehouseRef, {
          'capacity.used': increment(-effectiveQuantity),
          'capacity.available': increment(effectiveQuantity),
          'stats.totalStock': increment(-effectiveQuantity),
          updatedAt: serverTimestamp(),
        });

        // 3c. Update or create destination inventory
        const destinationData: Record<string, any> = {
          warehouseId: toWarehouseId,
          productId,
          productName: fromInventoryData.productName,
          variantId: variantId || null,
          variantName: fromInventoryData.variantName || null,
          quantity: newDestQuantity,
          minStockLevel: fromInventoryData.minStockLevel,
          maxStockLevel: fromInventoryData.maxStockLevel,
          sku: fromInventoryData.sku,
          price: fromInventoryData.price,
          avgCost: newDestAvgCost,
          discount: fromInventoryData.discount || 0,
          isOutOfStock: newDestQuantity === 0,
          isLowStock: newDestQuantity > 0 && newDestQuantity <= minStockLevel,
          isOverStock: newDestQuantity > 0 && newDestQuantity >= maxStockLevel,
          lastUpdated: serverTimestamp(),
        };
        if (destSerials) {
          destinationData.availableSerials = destSerials;
        }

        transaction.set(toInventoryRef, destinationData, { merge: true });

        // 3d. Update destination warehouse capacity and stats
        transaction.update(toWarehouseRef, {
          'capacity.used': increment(effectiveQuantity),
          'capacity.available': increment(-effectiveQuantity),
          'stats.productCount': toInventoryDoc.exists() ? increment(0) : increment(1),
          'stats.totalStock': increment(effectiveQuantity),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error('Error transferring stock:', error);
      throw error;
    }
  }
}